import { useState } from 'react'
import { C } from './theme.js'
import { continueAnonymously, registerWithEmail, signInWithEmail, signInWithGoogle } from './auth.js'
import { trackEvent } from './firebase.js'
import { saveNotifyPrefs } from './notify.js'
import { newEmailToken } from './notifyPrefs.js'
import { authErrorMessage, authInput, authPrimary, authSecondary, authGuest, authLink, authError } from './authStyles.js'
import { keepLocaleChoice, useT } from './i18n.js'
import { noteLandingAct } from './landingVisit.js'

export function AuthPanel({ onComplete, showGuest = false, guestOnly = false, foldAccount = false, hideGoogle = false, surface = 'app' }) {
  const translate = useT()
  const [mode, setMode] = useState('choices')
  const [unfolded, setUnfolded] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  /* Unticked by default, and it stays that way. An opt-in that arrives
   * pre-ticked is not one, and `recap.email` is the flag the server trusts
   * before it will mail anybody. */
  const [emailOptIn, setEmailOptIn] = useState(false)

  const run = async (action, method, { optInToEmail = false } = {}) => {
    setBusy(true)
    setError('')
    trackEvent('auth_attempted', { method, surface })
    if (surface === 'landing') {
      noteLandingAct(method === 'anonymous' ? 'guest' : method)
      // Before the action, not after: the app mounts and reads its settings
      // the moment auth resolves, which can be before this function resumes.
      keepLocaleChoice()
    }
    const result = await action()
    setBusy(false)
    if (result.error) {
      trackEvent('auth_failed', { method, surface })
      setError(authErrorMessage(result.error))
    } else if (!result.cancelled) {
      if (optInToEmail) {
        /* Written after the account exists, so it carries a fresh `updatedAt`
         * and wins the newest-wins merge against the account's empty prefs.
         * The token is minted here rather than at first send: without one the
         * unsubscribe link in the footer would be dead, and the server skips
         * anybody who has not got one. */
        saveNotifyPrefs({ recap: { email: true }, emailToken: newEmailToken() })
        trackEvent('email_opt_in', { surface, at: 'signup' })
      }
      trackEvent(method === 'anonymous' ? 'session_started' : 'account_authenticated', { method, surface })
      onComplete?.()
    }
  }

  if (mode === 'email') {
    const register = false
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault()
          run(() => register ? registerWithEmail(email.trim(), password) : signInWithEmail(email.trim(), password), 'email')
        }}
        style={{ display: 'grid', gap: 10 }}
      >
        <label style={{ display: 'grid', gap: 5, color: C.sub, fontSize: 12 }}>
          {translate('auth.email')}
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required style={authInput} />
        </label>
        <label style={{ display: 'grid', gap: 5, color: C.sub, fontSize: 12 }}>
          {translate('auth.password')}
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="current-password" minLength="6" required style={authInput} />
        </label>
        {error ? <div style={authError}>{error}</div> : null}
        <button type="submit" disabled={busy} style={authPrimary}>{busy ? translate('auth.signingIn') : translate('auth.signInEmail')}</button>
        <button type="button" onClick={() => { setMode('register'); setError('') }} style={authSecondary}>{translate('auth.createAccount')}</button>
        <button type="button" onClick={() => { setMode('choices'); setError('') }} style={authLink}>{translate('auth.back')}</button>
      </form>
    )
  }

  if (mode === 'register') {
    return (
      <form
        onSubmit={(event) => {
          event.preventDefault()
          run(() => registerWithEmail(email.trim(), password), 'email', { optInToEmail: emailOptIn })
        }}
        style={{ display: 'grid', gap: 10 }}
      >
        <label style={{ display: 'grid', gap: 5, color: C.sub, fontSize: 12 }}>
          {translate('auth.email')}
          <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required style={authInput} />
        </label>
        <label style={{ display: 'grid', gap: 5, color: C.sub, fontSize: 12 }}>
          {translate('auth.password')}
          <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete="new-password" minLength="6" required style={authInput} />
        </label>
        {/* The only place the app ever asks. Settings has the same switch, but
          * nobody goes looking for a setting they do not know exists — which is
          * why one account in twenty-eight had email on. */}
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', color: C.sub, fontSize: 12, lineHeight: 1.45 }}>
          <input
            type="checkbox"
            checked={emailOptIn}
            onChange={(event) => setEmailOptIn(event.target.checked)}
            style={{ marginTop: 2 }}
          />
          <span>{translate('auth.recapOptIn')}</span>
        </label>
        {error ? <div style={authError}>{error}</div> : null}
        <button type="submit" disabled={busy} style={authPrimary}>{busy ? translate('auth.creatingAccount') : translate('auth.createAccountSubmit')}</button>
        <button type="button" onClick={() => { setMode('email'); setError('') }} style={authSecondary}>{translate('auth.haveAccount')}</button>
        <button type="button" onClick={() => { setMode('choices'); setError('') }} style={authLink}>{translate('auth.back')}</button>
      </form>
    )
  }

  /* Where a guest start is offered (the landing), it comes first and is the
   * primary button. Most landing visitors arrive on a phone from a link, and a
   * first screen whose first three buttons all ask for an account reads as a
   * sign-up wall: on Vigilia's ads, that was where phone visitors left, in
   * about two seconds. An account stays one tap away, under it, and the app
   * offers one again once there is progress worth keeping. */
  const guest = showGuest ? <button type="button" disabled={busy} onClick={() => run(continueAnonymously, 'anonymous')} style={authPrimary}>{translate('auth.analyzeMyGames')}</button> : null
  /* The landing's end-of-page button: one start, no account options again. */
  if (guestOnly) return <div style={{ display: 'grid', gap: 10 }}>{guest}{error ? <div style={authError}>{error}</div> : null}</div>
  /* The landing folds the account options under one link, so the first screen
   * asks for one decision. Someone with an account is looking for it and
   * finds the link; someone new should not have to read past three ways to
   * make one. */
  if (foldAccount && !unfolded) {
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {guest}
        <button type="button" onClick={() => setUnfolded(true)} style={authLink}>{translate('auth.haveAccount')}</button>
        {error ? <div style={authError}>{error}</div> : null}
      </div>
    )
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {guest}
      {/* Google refuses to sign in inside an app's built-in browser (Reddit's
          is a WebView), so there the button could only fail. Email still works. */}
      {hideGoogle ? null : <button type="button" disabled={busy} onClick={() => run(signInWithGoogle, 'google')} style={showGuest ? authSecondary : authPrimary}>{busy ? translate('auth.openingGoogle') : translate('auth.continueGoogle')}</button>}
      <button type="button" onClick={() => { if (surface === 'landing') noteLandingAct('email'); setMode('email') }} style={authSecondary}>{translate('auth.signInEmail')}</button>
      <button type="button" onClick={() => { if (surface === 'landing') noteLandingAct('email'); setMode('register') }} style={showGuest ? authGuest : authSecondary}>{translate('auth.createEmailAccount')}</button>
      {error ? <div style={authError}>{error}</div> : null}
    </div>
  )
}
